import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Send, Building2, PartyPopper } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import MatchScore from "@/components/MatchScore";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function SocialProof() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/onboarding/preview-matches/${id}`);
        setMatches(data);
      } catch (e) {
        toast.error(t("social_proof.toast_calc_failed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  const contact = async (m) => {
    try {
      await api.post("/api/contacts", {
        to_user_id: m.brief.owner_id,
        project_id: id,
        brief_id: m.brief.id,
        message: t("social_proof.pitch_message", { score: Math.round(m.score), title: m.brief.title }),
      });
      toast.success(t("social_proof.toast_sent", { name: m.sponsor?.commercial_name || t("social_proof.sponsor_generic") }));
    } catch (e) {
      toast.error(t("social_proof.toast_send_failed"));
    }
  };

  return (
    <DashboardLayout
      title={t("social_proof.title")}
      subtitle={t("social_proof.subtitle")}
      actions={
        <Link to="/dashboard/proyectos" data-testid="link-continue" className="text-petroleum hover:text-gold inline-flex items-center gap-1">
          {t("social_proof.continue")} <ArrowRight size={14} />
        </Link>
      }
    >
      <div className="kpi-card p-8 mb-8 bg-gradient-to-br from-petroleum to-petroleum/90 text-white border-0">
        <PartyPopper size={32} className="text-gold" />
        <h2 className="font-display text-3xl mt-3">{t("social_proof.published_title")}</h2>
        <p className="text-white/70 mt-2 max-w-2xl">
          {t("social_proof.published_desc")}
        </p>
      </div>

      {loading ? (
        <div className="text-slate-500">{t("social_proof.calculating")}</div>
      ) : matches.length === 0 ? (
        <div className="kpi-card p-10 text-center">
          <Sparkles size={28} className="text-gold mx-auto" />
          <h3 className="font-display text-2xl text-petroleum mt-3">{t("social_proof.no_matches_title")}</h3>
          <p className="text-slate-500 mt-2">{t("social_proof.no_matches_desc")}</p>
          <Button onClick={() => nav("/dashboard")} className="mt-6 bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
            {t("social_proof.back_to_dashboard")}
          </Button>
        </div>
      ) : (
        <div className="grid gap-5">
          {matches.map((m, i) => (
            <div key={i} className="kpi-card p-6 flex flex-col md:flex-row gap-6 items-start" data-testid={`row-social-match-${i}`}>
              <MatchScore score={m.score} size="lg" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-slate-500 inline-flex items-center gap-2">
                  <Building2 size={12} /> {m.sponsor?.commercial_name || t("social_proof.sponsor_default")}
                </p>
                <h3 className="font-display text-2xl text-petroleum mt-1">{m.brief.title}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{m.brief.description}</p>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {m.reasons.slice(0, 4).map((r, j) => (
                    <div key={j} className="border border-slate-200 rounded-lg px-3 py-2">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">{r.label}</p>
                      <div className="text-lg font-display text-petroleum">{r.score}%</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-3">
                  <Button onClick={() => contact(m)} data-testid={`button-contact-${i}`} className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
                    <Send size={14} className="mr-2" /> {t("social_proof.request_contact")}
                  </Button>
                  <Link to={`/brief/${m.brief.id}`} className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-1 self-center">
                    {t("social_proof.view_full_brief")} <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
